import styled from 'styled-components'
import { H3, H4, P } from '../../../shared_components/Library/Typeography'
import { Box } from '../../../shared_components/UI/Box'
import { useSwipeable } from 'react-swipeable'
import { useEffect, useRef, useState } from 'react'
import { StyledThemedButton } from '../../../shared_components/ThemedButton/styles'
import { TokenSpinner } from '../StakingHero'

type OptionValue = number

type Option = {
  value: OptionValue
  header: string
  items: {
    title: string
    description: string
  }[]
  apy: string
  multiplier: string
}

function BenefitsOptions({
  options,
  highlightedOption,
}: {
  options: Option[]
  highlightedOption: OptionValue
}) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(1)
  const optionsRef = useRef<HTMLDivElement[]>([]) // Array of refs for each option card
  const slidersRef = useRef<HTMLDivElement>(null)

  const handleNext = () => {
    if (currentSlideIndex < options.length - 1) {
      const nextIndex = currentSlideIndex + 1
      setCurrentSlideIndex(nextIndex)
      scrollToOption(nextIndex)
    } else {
      setCurrentSlideIndex(0)
      scrollToOption(0)
    }
  }

  const handlePrev = () => {
    if (currentSlideIndex > 0) {
      const prevIndex = currentSlideIndex - 1
      setCurrentSlideIndex(prevIndex)
      scrollToOption(prevIndex)
    } else {
      setCurrentSlideIndex(options.length - 1)
      scrollToOption(options.length - 1)
    }
  }

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrev,
  })

  const scrollToOption = (index: number) => {
    const card = optionsRef.current[index]
    const slider = slidersRef.current

    if (card && slider) {
      const cardOffsetLeft = card.offsetLeft
      const cardWidth = card.offsetWidth
      const containerWidth = slider.offsetWidth

      // Scroll the container horizontally to center the selected card
      slider.scrollTo({
        left: cardOffsetLeft - containerWidth / 2 + cardWidth / 2,
        behavior: 'smooth',
      })
    }
  }

  // Scroll to default highlighted option
  useEffect(() => {
    const defaultOptionIndex = options.findIndex(
      (option) => option.value === highlightedOption,
    )
    setCurrentSlideIndex(defaultOptionIndex)
    scrollToOption(defaultOptionIndex) // Option 2 is at index 1
  }, [options])

  return (
    <Box dc>
      <OptionsCont
        padding="var(--Index-Grids-Gutters, 32px) var(--units-0, 0px)"
        g="var(--Index-Grids-Gutters, 32px);"
        jc
        {...swipeHandlers}
      >
        <ScrollContainer ref={slidersRef}>
          {options.map(({ value, header, items, apy, multiplier }, i) => {
            const isHighlighted = value === highlightedOption
            return (
              <Option
                key={i}
                data-index={i}
                dc
                isHighlighted={value === highlightedOption}
                ref={(el) => (optionsRef.current[i] = el)}
              >
                <Header isHighlighted={isHighlighted}>
                  <H3
                    s="XL"
                    fw="bolder"
                    c={isHighlighted ? 'text-invert' : 'text-primary'}
                    ff="Proxima Nova"
                    uc
                  >
                    {header}
                  </H3>
                  <TokenSpinner />
                </Header>
                <Box dc pad="l" g={20} z={2}>
                  <Box dc g={24}>
                    {items.map(({ title, description }, i) => (
                      <Box key={i} g={6}>
                        <div style={{ marginTop: '9px' }}>
                          <Dot
                            size={7}
                            c={
                              isHighlighted
                                ? 'var(--text-invert, #080808)'
                                : 'var(--text-secondary, #B1B1B1)'
                            }
                          />
                        </div>
                        <Box dc g={6}>
                          <H4
                            s="M"
                            fw="semibold"
                            c={isHighlighted ? 'text-invert' : 'text-primary'}
                            ls="-0.5px"
                          >
                            {title}
                          </H4>
                          <P
                            s="M"
                            fw={isHighlighted ? 'bolder' : 'regular'}
                            c={isHighlighted ? 'text-invert' : 'text-primary'}
                          >
                            {description}
                          </P>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Box g={16}>
                    {apy && (
                      // padding and gaps are not the same as design because design code is... wrong ¯\_(ツ)_/¯
                      // padding and gap are measured up to the 1st pixel of the letter, without considering line height
                      <ApyBox
                        dc
                        minW="156px"
                        minH="75px"
                        jc
                        padding="0 var(--units-16, 16px)"
                      >
                        <H4
                          s="XL"
                          fw="bolder"
                          c={isHighlighted ? 'text-invert' : 'text-primary'}
                          ff="Proxima Nova"
                          uc
                        >
                          {apy}
                        </H4>
                        <P
                          s="S"
                          fw={isHighlighted ? 'semibold' : 'regular'}
                          c={isHighlighted ? 'text-invert' : 'text-primary'}
                        >
                          APY
                        </P>
                      </ApyBox>
                    )}
                    {multiplier && (
                      // padding and gaps are not the same as design because design code is... wrong ¯\_(ツ)_/¯
                      // padding and gap are measured up to the 1st pixel of the letter, without considering line height
                      <MultiplierBox
                        dc
                        minH="75px"
                        jc
                        padding="0 var(--units-14, 14px)"
                        isHighlighted={isHighlighted}
                      >
                        <H4
                          s="XL"
                          fw="bolder"
                          c={
                            isHighlighted ? 'text-brandPrimary' : 'text-primary'
                          }
                          ff="Proxima Nova"
                          uc
                        >
                          {multiplier}
                        </H4>
                        <P
                          s="S"
                          fw="semibold"
                          c={
                            isHighlighted ? 'text-brandPrimary' : 'text-primary'
                          }
                        >
                          Reward multiplier
                        </P>
                      </MultiplierBox>
                    )}
                  </Box>
                </Box>
              </Option>
            )
          })}
        </ScrollContainer>
      </OptionsCont>
      <Mobile>
        <Box g={'var(--Spacing-Padding-L, 16px)'} jc ac>
          <StyledThemedButton
            $styleType="tertiary"
            bss={'enabled'}
            bst={'alpha'}
            onClick={handlePrev}
          >
            <img src="/img/icons/arrow/left.svg" alt="Previous" />
          </StyledThemedButton>
          <Box flex={0} g={'var(--units-8, 8px)'}>
            {options.map((o, i) => (
              <Indicator key={i} isActive={i === currentSlideIndex} />
            ))}
          </Box>
          <StyledThemedButton
            $styleType="tertiary"
            bss={'enabled'}
            bst={'alpha'}
            onClick={handleNext}
          >
            <img src="/img/icons/arrow/right.svg" alt="Previous" />
          </StyledThemedButton>
        </Box>
      </Mobile>
    </Box>
  )
}

export default BenefitsOptions

const Indicator = styled.div<{ isActive?: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 300px;
  background: var(--surface-minimal-alpha-tertiary, rgba(255, 255, 255, 0.4));

  ${({ isActive }) =>
    isActive &&
    `
    background: var(--surface-brand, #FF0);
  `}
`

const Mobile = styled.div`
  width: 100%;
  @media screen and (min-width: 1000px) {
    display: none;
  }
`

const OptionsCont = styled(Box)`
  align-items: center;
  overflow: hidden; /* Hide scrollbar */
  position: relative;
  width: 100%;
  overflow: hidden;
`

const ScrollContainer = styled(Box)`
  padding: var(--Index-Grids-Gutters, 32px) var(--units-0, 0px);
  gap: var(--Index-Grids-Gutters, 32px);
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory; /* Ensures that cards snap into position when scrolling */
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`

const Option = styled(Box)<{ isHighlighted: boolean }>`
  border-radius: var(--units-12, 12px);
  border: var(--units-1, 1px) solid var(--stroke-secondary, #333);
  background: var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04));
  min-width: 346px;
  flex: unset;

  ${({ isHighlighted }) =>
    isHighlighted &&
    `
      --dotSize: 1px;

      background: var(--surface-brand, #FFF00);
      position: relative;

      ::after {
        /* Cover our element */
        content: '';
        position: absolute;
        inset: 0;
        opacity: 0.3;

        /* Dotted background */
        background-image: radial-gradient(
          circle at center,
          black var(--dotSize),
          transparent 0
        );
        background-size: 4px 4px;
        background-repeat: round;
        background-position: 0 0, 0.65rem 0.65rem;
        mask-image: linear-gradient(270deg, rgb(0 0 0),rgb(0 0 0 / 0) 70%);
        //mask-image: linear-gradient(rgb(0 0 0), rgb(0 0 0 / 0));

        z-index: 1;
      }
  `}

  > * {
    z-index: 2;
  }
`

const Header = styled.div<{ isHighlighted: boolean }>`
  background: var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04));
  padding: var(--units-24, 24px) var(--units-20, 20px);
  width: 100%;
  height: 69px;
  display: flex;
  align-items: center;
  position: relative;

  .token-single {
    display: none;

    @media screen and (min-width: 1000px) {
      position: absolute;
      top: -35px;
      right: 0;
      width: 160px;
      height: 70px;
      transform: rotateZ(45deg);
    }

    @media screen and (max-width: 999px) {
      position: absolute;
      top: -20px;
      right: 50px;
    }
  }

  ${({ isHighlighted }) =>
    isHighlighted &&
    `
      background-color: rgba(0, 0, 0, 0.04);
      background-image: url('/zap-benefit-header.png');

      .token-single {
        display: block;
      }

      //::after {
      //  content: '';
      //  position: absolute;
      //  top: -9px;
      //  right: 51px;
      //  background-image: url('/zap-coin-white-transparent-bg.svg');
      //  background-size: contain;
      //  width: 67.813px;
      //  height: 40.458px;
      //}
    `}
`

const ApyBox = styled(Box)`
  border-radius: var(--units-4, 4px);
  border: var(--units-1, 1px) solid var(--stroke-secondary, #333);
  background: var(--states-invert-alpha-16, rgba(0, 0, 0, 0.16));
`

const MultiplierBox = styled(Box)<{ isHighlighted: boolean }>`
  border-radius: var(--units-4, 4px);
  border: var(--units-1, 1px) solid var(--stroke-secondary, #333);
  background: var(--surface-minimal-alpha-secondary, rgba(255, 255, 255, 0.08));

  ${({ isHighlighted }) =>
    isHighlighted &&
    `
      background: #080808;
      --dotSize: 0.5px;

      position: relative;

      ::after {
        /* Cover our element */
        content: '';
        position: absolute;
        inset: 0;
        opacity: 0.3;

        /* Dotted background */
        background-image: radial-gradient(
          circle at center,
          var(--surface-brand, #FFF00) var(--dotSize),
          transparent 0
        );
        background-size: 2px 2px;
        background-repeat: round;
        background-position: 0 0, 0.65rem 0.65rem;
        mask-image: linear-gradient(45deg, rgba(8, 8, 8, 0.00) 20.85%, #080808 74.06%);

        z-index: 1;
      }
  `}
`

const Dot = styled.div<{ size: number; c: string }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  border-radius: 50%;
  background: ${({ c }) => c};
`
